import { getStagingRowColumnValue, normalizeBareColumn } from "./stagingGridCellValue";

const fixKey = (clbId, columnName) => `${clbId}:${normalizeBareColumn(columnName)}`;

const isPendingAppliedFix = (fix, key, acceptedFixes, rejectedFixes) =>
    !acceptedFixes.has(key)
    && !rejectedFixes.has(key)
    && fix.status === "applied";

export const collectPendingSuggestions = (
    aiFixes,
    acceptedFixes,
    rejectedFixes,
    columnNameFilter,
) => {
    if (!aiFixes?.suggestions) return [];

    const candidates = [];
    for (const col of aiFixes.suggestions) {
        if (columnNameFilter && col.column_name !== columnNameFilter) continue;
        for (const fix of col.fixes || []) {
            const key = fixKey(fix.clb_id, col.column_name);
            if (!isPendingAppliedFix(fix, key, acceptedFixes, rejectedFixes)) continue;
            candidates.push({
                clbId: fix.clb_id,
                columnName: col.column_name,
                originalValue: fix.original_value ?? null,
                newValue: fix.new_value ?? null,
                fixMode: col.fix_mode ?? null,
                requiresReview: Boolean(fix.requires_review),
            });
        }
    }
    return candidates;
};

const getStagingRowId = (row) => String(
    row.CLB_ID ?? row.clb_id ?? row["CDM_CustomCSV.CLB_ID"] ?? "",
);

export const buildTranslationRuleKey = ({ columnName, originalValue, newValue }) =>
    JSON.stringify([columnName, originalValue, newValue ?? ""]);

export const getSelectedTranslationFixes = (translationRules, selectedRuleKeys) =>
    (selectedRuleKeys instanceof Set
        ? translationRules.filter(
            (rule) => selectedRuleKeys.has(buildTranslationRuleKey(rule)),
        )
        : translationRules
    ).flatMap((rule) => rule.fixes);

export const getFailedFixKeys = (errors = []) => new Set(
    errors
        .map((error) => {
            const match = error.match(/^clbId=(\d+)\s+column="([^"]+)"/);
            return match ? `${match[1]}:${match[2]}` : null;
        })
        .filter(Boolean),
);

/**
 * Splits AI fix candidates into bulk-replaceable translation rules and
 * one-off direct cell patches. When the AI provides fixMode, honor it;
 * otherwise fall back to the legacy heuristic (2+ identical replacements).
 */
export const partitionFixesForApplication = (
    candidates,
    stagingItems = [],
    totalRows = stagingItems.length,
) => {
    const translationGroups = new Map();
    const directFixes = [];

    for (const candidate of candidates) {
        const { columnName, originalValue, newValue, clbId, fixMode } = candidate;
        if (fixMode === "direct") {
            directFixes.push(candidate);
            continue;
        }

        const canTranslate = originalValue != null
            && String(originalValue).length > 0
            && clbId != null;

        if (!canTranslate) {
            directFixes.push(candidate);
            continue;
        }

        const groupKey = JSON.stringify([columnName, originalValue, newValue ?? ""]);
        const existing = translationGroups.get(groupKey);
        if (existing) {
            existing.fixes.push(candidate);
        } else {
            translationGroups.set(groupKey, {
                columnName,
                originalValue,
                newValue,
                fixMode,
                fixes: [candidate],
            });
        }
    }

    const translationRules = [];
    const groups = [...translationGroups.values()];
    const chainedColumns = new Set();
    for (const group of groups) {
        if (groups.some((other) =>
            other !== group
            && other.columnName === group.columnName
            && String(group.newValue ?? "") === String(other.originalValue),
        )) {
            chainedColumns.add(group.columnName);
        }
    }

    for (const group of groups) {
        const candidateIds = new Set(group.fixes.map((fix) => String(fix.clbId)));
        const matchingRows = stagingItems.filter((row) => (
            String(getStagingRowColumnValue(row, group.columnName) ?? "")
            === String(group.originalValue)
        ));
        const unapprovedMatchingRows = matchingRows.filter(
            (row) => !candidateIds.has(getStagingRowId(row)),
        );
        const hasCompleteStagingData = stagingItems.length === Number(totalRows);
        const aiChoseTranslation = group.fixes.every((fix) => fix.fixMode === "translation")
            || group.fixMode === "translation";
        const heuristicTranslation = group.fixes.length >= 2
            && group.fixes.every((fix) => !fix.fixMode);
        const matchingRowsAreApproved = aiChoseTranslation
            ? unapprovedMatchingRows.length === 0
            : hasCompleteStagingData
                && matchingRows.length === group.fixes.length
                && unapprovedMatchingRows.length === 0;

        if (
            (aiChoseTranslation || heuristicTranslation)
            && (aiChoseTranslation || hasCompleteStagingData)
            && matchingRowsAreApproved
            && !chainedColumns.has(group.columnName)
        ) {
            translationRules.push(group);
        } else {
            directFixes.push(...group.fixes);
        }
    }

    return { translationRules, directFixes };
};

export const buildTranslationRuleName = ({ columnName, originalValue, newValue }) =>
    `AI fix: ${columnName} "${originalValue}" → "${newValue ?? ""}"`;

export const buildTranslationActionTooltip = ({ columnName, originalValue, newValue, rowCount }) =>
    `This will create a rule that replaces '${originalValue}' → '${newValue ?? ""}' in column ${columnName} for all ${rowCount} matching rows.`;

export const buildPendingSuggestionCellKeys = (aiFixes, acceptedFixes, rejectedFixes) => {
    const keys = new Set();
    if (!aiFixes?.suggestions) return keys;

    for (const col of aiFixes.suggestions) {
        for (const fix of col.fixes || []) {
            const key = fixKey(fix.clb_id, col.column_name);
            if (!isPendingAppliedFix(fix, key, acceptedFixes, rejectedFixes)) continue;
            keys.add(key);
        }
    }
    return keys;
};

export const buildSuggestionDetailsByKey = (aiFixes, acceptedFixes, rejectedFixes) => {
    const details = new Map();
    if (!aiFixes?.suggestions) return details;

    for (const col of aiFixes.suggestions) {
        for (const fix of col.fixes || []) {
            const key = fixKey(fix.clb_id, col.column_name);
            if (!isPendingAppliedFix(fix, key, acceptedFixes, rejectedFixes)) continue;
            details.set(key, {
                clbId: fix.clb_id,
                columnName: normalizeBareColumn(col.column_name),
                confidence: col.confidence ?? null,
                originalValue: fix.original_value ?? null,
                newValue: fix.new_value ?? null,
                rationale: col.rationale ?? null,
                fixMode: col.fix_mode ?? null,
                requiresReview: Boolean(fix.requires_review),
            });
        }
    }
    return details;
};

export const buildSuggestionsByColumn = (suggestionCellKeys) => {
    const counts = new Map();
    for (const key of suggestionCellKeys) {
        const separatorIndex = key.indexOf(":");
        if (separatorIndex === -1) continue;
        const columnName = key.slice(separatorIndex + 1);
        counts.set(columnName, (counts.get(columnName) || 0) + 1);
    }
    return counts;
};

export const buildManualErrorsByColumn = (rows = [], suggestionCellKeys = new Set()) => {
    const counts = new Map();
    for (const row of rows) {
        const clbId = row.clb_id;
        for (const issue of row.issues || []) {
            if (issue.severity !== "error") continue;
            if (suggestionCellKeys.has(fixKey(clbId, issue.column))) continue;
            counts.set(issue.column, (counts.get(issue.column) || 0) + 1);
        }
    }
    return counts;
};

export const resolveStagingCellHighlight = ({
    clbId,
    bareColumn,
    issuesByClbId,
    suggestionCellKeys,
}) => {
    if (clbId == null) return "";
    if (suggestionCellKeys.has(fixKey(clbId, bareColumn))) {
        return "staging-ai-suggestion-cell";
    }
    const cellIssues = issuesByClbId.get(clbId) || [];
    const colIssues = cellIssues.filter(
        (issue) =>
            issue.column === bareColumn
            || `CDM_CustomCSV.${issue.column}` === bareColumn,
    );
    if (colIssues.length === 0) return "";
    return colIssues.some((issue) => issue.severity === "error")
        ? "staging-error-cell"
        : "staging-warning-cell";
};

export const canRequestAiFixes = ({ aiSuggestionsEnabled, onSuggestFixes }) =>
    aiSuggestionsEnabled && typeof onSuggestFixes === "function";

export const shouldRequestAiFixes = ({ trigger, hasSuggestions, busy }) => {
    if (busy) return false;
    if (trigger === "rerun") return true;
    return !hasSuggestions;
};

export const waitForTranslationCompletion = async ({
    translationId,
    listTranslations,
    profileId,
    projectId,
    previousRunAt = null,
    attempts = 15,
    intervalMs = 2000,
}) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        const translations = await listTranslations(profileId, projectId);
        const translation = translations?.find((item) => item.id === translationId);
        if (
            translation?.state !== "Processing"
            && translation?.last_run_at
            && translation.last_run_at !== previousRunAt
        ) {
            return translation;
        }
    }
    return null;
};

export const LATE_WATCH_ATTEMPTS = 24;
export const LATE_WATCH_INTERVAL_MS = 5000;

export const watchTranslationUntilComplete = async ({
    translationId,
    listTranslations,
    profileId,
    projectId,
    previousRunAt = null,
    isCancelled = () => false,
    attempts = LATE_WATCH_ATTEMPTS,
    intervalMs = LATE_WATCH_INTERVAL_MS,
}) => {
    for (let attempt = 0; attempt < attempts; attempt += 1) {
        if (isCancelled()) return null;
        const result = await waitForTranslationCompletion({
            translationId,
            listTranslations,
            profileId,
            projectId,
            previousRunAt,
            attempts: 1,
            intervalMs,
        }).catch(() => null);
        if (result) return result;
    }
    return null;
};

export { fixKey };
